import type { ScheduleColumnView } from '@/contexts/screenConfiguration.types';

// Shared by the monitor column and the screen-config admin form so the default view,
// default width, and width bounds are stated once.
export const DEFAULT_SCHEDULE_COLUMN_VIEW: ScheduleColumnView = 'LIST';
export const DEFAULT_SCHEDULE_COLUMN_WIDTH_PERCENT = 25;
export const MIN_SCHEDULE_COLUMN_WIDTH_PERCENT = 10;
export const MAX_SCHEDULE_COLUMN_WIDTH_PERCENT = 60;

// Maximum columns the schedule area may split into. Default 1 (and missing ⇒ 1) is bit-exact
// current single-column behavior. Admin input is bounded 1–4; a stored config may carry null,
// which the form and the monitor both coalesce to the default.
export const DEFAULT_SCHEDULE_COLUMN_MAX_COLUMNS = 1;
export const MIN_SCHEDULE_COLUMN_MAX_COLUMNS = 1;
export const MAX_SCHEDULE_COLUMN_MAX_COLUMNS = 4;

// Carousel page rotation interval in whole seconds. Missing values retain the existing 7s
// behavior; the admin input and runtime share the same safety bounds.
export const DEFAULT_SCHEDULE_COLUMN_CAROUSEL_INTERVAL_SECONDS = 7;
export const MIN_SCHEDULE_COLUMN_CAROUSEL_INTERVAL_SECONDS = 2;
export const MAX_SCHEDULE_COLUMN_CAROUSEL_INTERVAL_SECONDS = 60;

// Per-column width clamp for the multi-column layout, single-sourced with the packing math.
// MIN gates the sliver guard (effectiveMax = floor(areaWidth / MIN)); 180 admits columns down
// to roughly the v1 minimum column (10% of a ~1900px-CSS viewport), which render in the
// narrow/tall-card mode. MAX caps the rendered/candidate per-column width so a collapsing day
// doesn't stretch a card across half the wall — roughly the width a default 25%-viewport
// column has on the target kiosk. Tuned at visual QA 2026-07-14 on a 1912px-CSS viewport,
// where a ~40%-width area fits 4 columns; revisit per deployment if kiosk resolutions differ.
// The clamp is only active when the configured maximum is > 1 (a maxColumns = 1 screen keeps
// today's behavior bit-exact, including wide cards at scheduleColumnWidth = 60). Known cosmetic
// band: narrow derives from the full candidate width while non-first cells lose the 1rem gutter
// as inner padding, so for candidate widths within a gutter above the breakpoint those cards
// render the 2-row treatment slightly under it — ellipsis truncation absorbs the difference.
export const MIN_COLUMN_WIDTH_PX = 180;
export const MAX_COLUMN_WIDTH_PX = 480;

// Below this measured container width (px) arrived cards wrap from two rows to three. The
// column is a configurable 10–60% of the viewport, so the threshold is on the container's own
// measured width, not a viewport media query. Single source of truth for the CSS wrap and the
// packing/geometry math so the two never drift.
export const SCHEDULE_COLUMN_NARROW_BREAKPOINT_PX = 360;

// Card CSS geometry (rem), single-sourced with scheduleColumnPacking.ts's pixel strides so the
// Card height in ScheduleColumn.tsx and the packing math never drift.
export const CARD_HEIGHT_REM = 4.5;
export const CARD_HEIGHT_TALL_REM = 6.5;
export const CARD_GAP_REM = 0.5;

// Rendered height (rem) of the kiosk-LIST footer block that sits at the bottom of the last
// column's cell (ellipsis strip + gap + card + padding). Single-sourced with the packing
// module's LIST_FOOTER_RESERVE_PX (= rem × 16) exactly as CARD_HEIGHT_REM pairs with the card
// strides, so the footer's pinned CSS height and the fit reserve can never drift. Pinned at 5rem
// (80px) — the previously visual-QA'd reserve — so the footer's natural (organic) height is now
// deterministic and the last column's scroll viewport shrinks by exactly the reserved amount.
export const LIST_FOOTER_HEIGHT_REM = 5;

// Rendered height (rem) of the SAME footer block on a narrow column, where the `+N more · last
// case HH:mm` text is deliberately stacked as two lines (`+N more` / `last case HH:mm`) so it
// cannot wrap unpredictably and spill past the pinned block on very narrow columns. The extra
// text line needs ~1.25rem over the single-line 5rem block (measured: two 1.125rem text lines +
// the FooterCard's 0.75rem vertical padding + the ellipsis strip + gaps ≈ 6.2rem); pinned at
// 6.5rem, matching the tall-card idiom (CARD_HEIGHT_TALL_REM), for headroom. Paired with the
// packing module's LIST_FOOTER_RESERVE_TALL_PX (= rem × 16). The footer's pinned height is a pure
// function of `narrow` (never of the text content) so the scroll viewport above it cannot flap as
// the count collapses.
export const LIST_FOOTER_HEIGHT_TALL_REM = 6.5;
